import { useState } from 'react';
import { events, categories } from '@/mocks/gossip';

interface EventGridProps {
  query: string;
}

export default function EventGrid({ query }: EventGridProps) {
  const [cat, setCat] = useState('全部');
  const [local, setLocal] = useState('');

  const keyword = (local || query || '').trim().toLowerCase();
  const list = events.filter((e) => {
    const okCat = cat === '全部' || e.category === cat;
    const okKw = !keyword || e.title.toLowerCase().includes(keyword) || e.summary.toLowerCase().includes(keyword) || e.tag.toLowerCase().includes(keyword);
    return okCat && okKw;
  });

  const statusStyle: Record<string, string> = {
    热议中: 'bg-primary-100 text-primary-700',
    进行中: 'bg-accent-100 text-accent-900',
    已完结: 'bg-secondary-100 text-secondary-800',
  };

  return (
    <section id="events" className="scroll-mt-24 py-10 md:py-14 bg-background-100/70">
      <div className="container-site">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-1.5 rounded-full bg-primary-500"></span>
              <span className="text-xs font-medium tracking-widest text-foreground-500">HOT EVENT ROUNDUP</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">热点<span className="text-primary-600">事件盘点</span></h2>
          </div>
          <div className="w-full md:w-72">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder="在本区筛选事件…"
                className="w-full h-11 pl-11 pr-4 rounded-full bg-background-50 border border-background-200 text-sm outline-none focus:border-primary-400"
              />
            </div>
          </div>
        </div>

        {/* 分类筛选 */}
        <div className="flex items-center gap-1.5 flex-wrap mb-7">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-1.5 rounded-full text-sm transition whitespace-nowrap ${
                cat === c ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* 事件卡片 */}
        {list.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 p-14 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">没有找到相关事件，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((item, idx) => (
              <article
                key={item.id}
                className="group rounded-2xl border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 hover:-translate-y-1 transition duration-300 animate-fade-up"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="relative h-36 bg-gradient-to-br from-primary-100 via-accent-100 to-background-100 flex items-center justify-center overflow-hidden">
                  <span className="font-display text-[88px] text-primary-500/20 leading-none absolute -right-2 -top-3">
                    {String(item.id).padStart(2, '0')}
                  </span>
                  <span className="relative w-16 h-16 rounded-2xl bg-primary-500 text-background-50 flex items-center justify-center rotate-6 group-hover:rotate-0 transition duration-300">
                    <i className="ri-flashlight-fill text-2xl"></i>
                  </span>
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-600 text-background-50 text-xs font-medium">
                    {item.tag}
                  </span>
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-xs text-foreground-400">{item.date}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs ${statusStyle[item.status] || 'bg-secondary-100 text-secondary-800'}`}>
                      {item.status}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">{item.summary}</p>
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-background-100 text-xs text-foreground-400">
                    <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i> {item.views}</span>
                    <span className="flex items-center gap-1.5"><i className="ri-chat-3-line"></i> {item.comments}</span>
                    <span className="flex items-center gap-1 text-primary-600 font-medium">
                      <i className="ri-fire-fill"></i> 热度 {item.heat}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}